import { getApi, postApi, postApiWithoutBody, putApi } from '../../Shared/Services/RestService';
import {
  CandidateDataRow,
  CreateShareRequest,
  DataRow,
  DataShare,
  DataShareEvent,
  DataSearchRequest,
  DataSearchResponse,
  OnboardRequest,
  PublicShareView,
  RecruiterDataRow,
  TalentAnalytics,
  TalentStats,
  UpdateShareRequest,
} from '../Types/CandidateDataType';

const ADMIN = '/admin/support/candidate-data';
const PUBLIC = '/public/candidate-data';

/* ==========================================================================
   Admin directory, shares and tracking
   ========================================================================== */

export const searchCandidateData = (params: DataSearchRequest) => {
  return getApi<DataSearchResponse<DataRow>>(`${ADMIN}/search`, params);
};

export const getCandidateDataStats = (params: DataSearchRequest) => {
  return getApi<TalentStats>(`${ADMIN}/stats`, params);
};

export const getCandidateDataAnalytics = (params: DataSearchRequest) => {
  return getApi<TalentAnalytics>(`${ADMIN}/analytics`, params);
};

export const getDataShares = (includeDeleted = false) => {
  return getApi<DataShare[]>(`${ADMIN}/shares`, { includeDeleted });
};

export const createDataShare = (payload: CreateShareRequest) => {
  return postApi<DataShare>(`${ADMIN}/shares`, payload);
};

export const updateDataShare = (id: string, payload: UpdateShareRequest) => {
  return putApi<DataShare>(`${ADMIN}/shares`, id, payload);
};

export const revokeDataShare = (id: string) => {
  return postApiWithoutBody<DataShare>(`${ADMIN}/shares/${id}/revoke`);
};

/** Soft-deletes a share link; it leaves the normal listings but stays restorable. */
export const deleteDataShare = (id: string) => {
  return postApiWithoutBody<DataShare>(`${ADMIN}/shares/${id}/delete`);
};

/** Restores a soft-deleted share link back into the active listings. */
export const restoreDataShare = (id: string) => {
  return postApiWithoutBody<DataShare>(`${ADMIN}/shares/${id}/restore`);
};

export const getDataShareEvents = (id: string) => {
  return getApi<DataShareEvent[]>(`${ADMIN}/shares/${id}/events`);
};

export const onboardData = (payload: OnboardRequest) => {
  return postApi<{ sent: number }>(`${ADMIN}/onboard`, payload);
};

/* ==========================================================================
   Public share access
   ========================================================================== */

export const viewSharedDirectory = (token: string, params: DataSearchRequest) => {
  return getApi<PublicShareView<DataRow>>(`${PUBLIC}/share/${encodeURIComponent(token)}`, params);
};

/** Full "Talent Insights" catalogue for a share; same token gate as the view, no tracking side effects. */
export const viewSharedDirectoryAnalytics = (token: string, params: DataSearchRequest) => {
  return getApi<TalentAnalytics>(`${PUBLIC}/share/${encodeURIComponent(token)}/analytics`, params);
};

export const trackShareEvent = (
  token: string,
  payload: { eventType: string; filtersJson?: string; metadata?: string }
) => {
  return postApi<null>(`${PUBLIC}/share/${encodeURIComponent(token)}/event`, payload);
};

export const requestOnboard = (token: string, payload: { message?: string }) => {
  return postApi<{ accepted: number }>(`${PUBLIC}/share/${encodeURIComponent(token)}/onboard`, payload);
};

/* Re-exported for callers that need the concrete row shape. */
export type { CandidateDataRow, RecruiterDataRow, DataRow };
